import type { DetroitGameState, SelectActionArgs, DetroitActionType } from '../../types';
import { debug } from '../../utils/debug';
import { getDetroitActionFee } from '../../types';
import type { Ctx } from 'boardgame.io';

export interface ValidationResult {
  success: boolean;
  error?: string;
  message?: string;
}

/**
 * アクション選択のバリデーション（Detroit固有）
 * @param G - Detroitゲーム状態
 * @param ctx - Boardgame.ioコンテキスト
 * @param args - アクション選択引数
 * @returns バリデーション結果
 */
export function validateSelectActionDetroit(
  G: DetroitGameState,
  _ctx: Ctx,
  args: SelectActionArgs
): ValidationResult {
  const player = G.players['0'];

  // Detroit専用アクションチェック（3種類のみ）
  const validActions: DetroitActionType[] = [
    'ENGINEER',
    'LOCOMOTIVE',
    'URBANIZATION'
  ];

  if (!validActions.includes(args.action as DetroitActionType)) {
    return {
      success: false,
      error: 'INVALID_ACTION_DETROIT',
      message: `Action ${args.action} is not available in Detroit solo play. Valid actions: ${validActions.join(', ')}`
    };
  }

  const action = args.action as DetroitActionType;

  // ディスク残数チェック
  const disksRemaining = G.actionDisks[action];
  if (disksRemaining <= 0) {
    // 使用可能なアクションをリストアップ
    const availableActions = validActions.filter(
      a => G.actionDisks[a] > 0
    );

    return {
      success: false,
      error: 'NO_DISKS_REMAINING',
      message: `Action ${action} has no disks remaining (already used 3 times). Available actions: ${availableActions.join(', ')}`
    };
  }

  // 手数料支払い可能かチェック
  const fee = getDetroitActionFee(disksRemaining);
  if (player.cash < fee) {
    // 現金不足の場合は株式発行で調達可能かチェック（ここでは警告のみ）
    // 実際の支払いはmoveで行い、不足分は収入レベルから減算
    debug.log(`[validateSelectActionDetroit] Warning: Player has $${player.cash}, action fee is $${fee}`);
  }

  // Locomotive選択時の機関車レベル上限チェック
  // Detroit: 選択は可能だが、レベルが最大(6)の場合はレベルは上がらない
  if (action === 'LOCOMOTIVE' && player.locomotiveLevel >= 6) {
    debug.log(`[validateSelectActionDetroit] Warning: Locomotive at max level (6), action will be selected but no upgrade`);
  }

  return { success: true };
}

/**
 * 選択可能なアクションのリストを取得
 * @param G - Detroitゲーム状態
 * @returns 選択可能なアクションの配列
 */
export function getAvailableActionsDetroit(G: DetroitGameState): DetroitActionType[] {
  const allActions: DetroitActionType[] = ['ENGINEER', 'LOCOMOTIVE', 'URBANIZATION'];
  return allActions.filter(action => G.actionDisks[action] > 0);
}

/**
 * アクションの手数料を取得
 * @param G - Detroitゲーム状態
 * @param action - アクション種別
 * @returns 手数料（$1-$3）、ディスクがない場合は0
 */
export function getActionFeeDetroit(G: DetroitGameState, action: DetroitActionType): number {
  const disksRemaining = G.actionDisks[action];
  return getDetroitActionFee(disksRemaining);
}
